// src/components/services/TenantSettingsService.ts
import Project from '../../common/Utils/project';
import api from './apiService';

export interface GetCustomerName {
  tenantName: [string];
}

export interface DefatomAgentSettings {
  agentVersion: string;
  versionReleaseDate: string;
  numberOfDaysForIdentifyingDisconncetedEndpoint: number;
  maxEndpointDisconnectedThresholdInDays: number;
  thresholdInMinutesForTaggingAgentAsOffline: number;
  thresholdInMinutesForTaggingAgentAsOfflineForRemediationPlan: number;
  useVendorPackageRepoAsPrimary: boolean; // Add this line
}

export interface RemediationSettings {
  numberOfSoftwareVersionsAvailableForRemediation: number;
}

export interface ComplianceSettings {
  currentPreviousDurationInDays: number;
  nonComplainceDefinitionDays: number;
}

export interface SeverityCVEMapping {
  severity: string;
  cveScore: string;
}

export interface PatchBySeverityConfiguration {
  severityCVEMapping: SeverityCVEMapping[];
  daysRange: string[];
}

export interface ResponseModel<T> {
  isSuccess: boolean;
  message: string;
  data: string | T; // data can be a JSON string or object
  errorCode?: string | null;
}

// New interfaces for bulk update API
export interface TenantSettingUpdate {
  settingPath: string;
  settingValue: string;
}

export interface UpdateTenantSettingsRequest {
  tenantId: string;
  tenantName: string;
  settings: TenantSettingUpdate[];
}

export type ReportExpiryMinutes = Record<string, number>;

export interface ReportsSettings {
  ReportListingThresholdInDays: number;
  ReportExpiryMinutes: ReportExpiryMinutes;
}

export interface DashboardTenant {
  tenantId: string;
  tenantName: string;
  customerType?: string;
  status?: string;
  isGlobalTenant?: boolean;
}

// featureList: one entry per feature, e.g. [{ "ApplicationBlackList": { "enabled": true } }, { "remoteAccess": { "splashtop": { ... } } }].
// It's an array, so TenantAPI never merges it with the global one: whatever is saved for a tenant is the tenant's whole list.
export type FeatureListEntry = Record<string, unknown>;

export interface SplashtopRemoteAccessSettings {
  enabled: boolean;
  [key: string]: unknown; // groupIds, requestPermission, permissions, ... are kept as they are
}

export interface TenantSettingsDocument {
  id: string;
  tenantId: string;
  tenantName: GetCustomerName;
  settings: {
    defatomAgent?: DefatomAgentSettings;
    remediation?: RemediationSettings;
    complaince?: ComplianceSettings;
    patchBySeverityConfiguration?: PatchBySeverityConfiguration;
    reports?: ReportsSettings;
    [key: string]: any;
  };
}

// Helper function to parse data if it's a JSON string
const parseData = <T>(response: ResponseModel<T>): T => {
  if (typeof response.data === 'string') {
    return JSON.parse(response.data) as T;
  }
  return response.data as T;
};

// GET: Fetch the feature list (tenant's own list, or the global one when the tenant has none). An absent key comes back as
// success with empty data, which means "no features configured".
export const getFeatureList = async (tenantId: string): Promise<FeatureListEntry[]> => {
  const response = await api.get<ResponseModel<FeatureListEntry[]>>(`/tenantservice/TenantSetting/${tenantId}/settings/featureList`);

  if (!response.data.isSuccess) {
    throw new Error(response.data.message || 'Failed to fetch feature list');
  }
  if (response.data.data === '' || response.data.data == null) {
    return [];
  }

  const parsed = parseData(response.data);
  return Array.isArray(parsed) ? parsed : [];
};

// Property lookup that ignores case, matching how AgentAPI reads these settings.
const findKey = (obj: Record<string, unknown>, name: string): string | undefined =>
  Object.keys(obj).find((key) => key.toLowerCase() === name.toLowerCase());

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

/** Reads a property ignoring case; undefined when absent. */
export const getIgnoreCase = (obj: Record<string, unknown> | null | undefined, name: string): unknown => {
  if (!obj) return undefined;
  const key = findKey(obj, name);
  return key ? obj[key] : undefined;
};

/** featureList → remoteAccess → splashtop as stored (a copy), or null when the tenant has no Splashtop entry. */
export const getSplashtopSettings = (featureList: FeatureListEntry[] | null | undefined): Record<string, unknown> | null => {
  for (const entry of featureList ?? []) {
    const remoteAccess = getIgnoreCase(entry, 'remoteAccess');
    if (!isObject(remoteAccess)) continue;
    const splashtop = getIgnoreCase(remoteAccess, 'splashtop');
    return isObject(splashtop) ? JSON.parse(JSON.stringify(splashtop)) : null;
  }
  return null;
};

/** featureList → remoteAccess → splashtop.enabled; false when the entry is missing. */
export const isRemoteAccessEnabled = (featureList: FeatureListEntry[] | null | undefined): boolean =>
  getIgnoreCase(getSplashtopSettings(featureList), 'enabled') === true;

/**
 * A copy of featureList with the given Splashtop settings changed. Every other entry (e.g. ApplicationBlackList) and
 * every Splashtop setting not in `changes` (e.g. installMode) is kept as is; an existing key keeps its casing. When
 * there is no Splashtop entry yet, one is added from defaultSplashtopSettings, because the list is saved whole and
 * nothing is inherited from the global list.
 */
export const withSplashtopSettings = (
  featureList: FeatureListEntry[],
  changes: Record<string, unknown>,
  defaultSplashtopSettings: SplashtopRemoteAccessSettings,
): FeatureListEntry[] => {
  const updated: FeatureListEntry[] = JSON.parse(JSON.stringify(featureList ?? []));

  for (const entry of updated) {
    const remoteAccessKey = findKey(entry, 'remoteAccess');
    const remoteAccess = remoteAccessKey ? entry[remoteAccessKey] : undefined;
    if (!isObject(remoteAccess)) continue;
    const splashtopKey = findKey(remoteAccess, 'splashtop') ?? 'splashtop';
    const splashtop: Record<string, unknown> = isObject(remoteAccess[splashtopKey])
      ? { ...(remoteAccess[splashtopKey] as Record<string, unknown>) }
      : JSON.parse(JSON.stringify(defaultSplashtopSettings));
    for (const [name, value] of Object.entries(changes)) {
      splashtop[findKey(splashtop, name) ?? name] = value;
    }
    remoteAccess[splashtopKey] = splashtop;
    return updated;
  }

  updated.push({ remoteAccess: { splashtop: { ...JSON.parse(JSON.stringify(defaultSplashtopSettings)), ...changes } } });
  return updated;
};

/** withSplashtopSettings for the on/off switch only. */
export const withRemoteAccessEnabled = (
  featureList: FeatureListEntry[],
  enabled: boolean,
  defaultSplashtopSettings: SplashtopRemoteAccessSettings,
): FeatureListEntry[] => withSplashtopSettings(featureList, { enabled }, defaultSplashtopSettings);

// GET: Fetch Defatom Agent Settings
export const getDefatomAgentSettings = async (tenantId: string): Promise<DefatomAgentSettings> => {
  const response = await api.get<ResponseModel<DefatomAgentSettings>>(`/tenantservice/TenantSetting/${tenantId}/settings/defatomAgent`);

  if (!response.data.isSuccess) {
    throw new Error(response.data.message || 'Failed to fetch Defatom Agent settings');
  }

  return parseData(response.data);
};

// GET: Fetch Remediation Settings
export const getRemediationSettings = async (tenantId: string): Promise<RemediationSettings> => {
  const response = await api.get<ResponseModel<RemediationSettings>>(`/tenantservice/TenantSetting/${tenantId}/settings/remediation`);

  if (!response.data.isSuccess) {
    throw new Error(response.data.message || 'Failed to fetch Remediation settings');
  }

  return parseData(response.data);
};

// GET: Fetch Compliance Settings
export const getComplianceSettings = async (tenantId: string): Promise<ComplianceSettings> => {
  const response = await api.get<ResponseModel<ComplianceSettings>>(`/tenantservice/TenantSetting/${tenantId}/settings/complaince`);

  if (!response.data.isSuccess) {
    throw new Error(response.data.message || 'Failed to fetch Compliance settings');
  }

  return parseData(response.data);
};

// GET: Fetch Patch By Severity Configuration
export const getPatchBySeverityConfiguration = async (tenantId: string): Promise<PatchBySeverityConfiguration> => {
  const response = await api.get<ResponseModel<PatchBySeverityConfiguration>>(
    `/tenantservice/TenantSetting/${tenantId}/settings/patchBySeverityConfiguration`,
  );

  if (!response.data.isSuccess) {
    throw new Error(response.data.message || 'Failed to fetch Patch By Severity Configuration');
  }

  return parseData(response.data);
};

// GET: Fetch Reports Settings
export const getReportsSettings = async (tenantId: string): Promise<ReportsSettings> => {
  const response = await api.get<ResponseModel<ReportsSettings>>(`/tenantservice/TenantSetting/${tenantId}/settings/Reports`);

  if (!response.data.isSuccess) {
    throw new Error(response.data.message || 'Failed to fetch Reports settings');
  }

  return parseData(response.data);
};

// PUT: Update Defatom Agent Settings (Legacy - deprecated, use updateTenantSettingsBulk instead)
export const updateDefatomAgentSettings = async (
  tenantId: string,
  settings: Partial<DefatomAgentSettings>,
): Promise<ResponseModel<any>> => {
  const response = await api.put<ResponseModel<any>>(`/tenantservice/TenantSetting/${tenantId}/settings/defatomAgent`, settings);

  return response.data;
};

// PUT: Update Remediation Settings (Legacy - deprecated, use updateTenantSettingsBulk instead)
export const updateRemediationSettings = async (tenantId: string, settings: RemediationSettings): Promise<ResponseModel<any>> => {
  const response = await api.put<ResponseModel<any>>(`/tenantservice/TenantSetting/${tenantId}/settings/remediation`, settings);

  return response.data;
};

// PUT: Update Compliance Settings (Legacy - deprecated, use updateTenantSettingsBulk instead)
export const updateComplianceSettings = async (tenantId: string, settings: ComplianceSettings): Promise<ResponseModel<any>> => {
  const response = await api.put<ResponseModel<any>>(`/tenantservice/TenantSetting/${tenantId}/settings/complaince`, settings);

  return response.data;
};

// PUT: Update Patch By Severity Configuration (Legacy - deprecated, use updateTenantSettingsBulk instead)
export const updatePatchBySeverityConfiguration = async (
  tenantId: string,
  configuration: PatchBySeverityConfiguration,
): Promise<ResponseModel<any>> => {
  const response = await api.put<ResponseModel<any>>(
    `/tenantservice/TenantSetting/${tenantId}/settings/patchBySeverityConfiguration`,
    configuration,
  );

  return response.data;
};

// Fetch all settings at once
export const getAllTenantSettings = async (tenantId: string) => {
  try {
    const [defatomAgent, remediation, compliance, patchSeverity, reports, featureList] = await Promise.all([
      getDefatomAgentSettings(tenantId),
      getRemediationSettings(tenantId),
      getComplianceSettings(tenantId),
      getPatchBySeverityConfiguration(tenantId),
      getReportsSettings(tenantId),
      // null = couldn't be read. The page then shows remote access as unavailable and never saves featureList, because
      // saving a list built from nothing would wipe the tenant's other features.
      getFeatureList(tenantId).catch((error) => {
        console.error('Error fetching feature list:', error);
        return null;
      }),
    ]);

    return {
      defatomAgent,
      remediation,
      complaince: compliance,
      patchBySeverityConfiguration: patchSeverity,
      reports,
      featureList,
    };
  } catch (error) {
    console.error('Error fetching tenant settings:', error);
    throw error;
  }
};

// NEW: Bulk Update API - Update multiple settings in a single request
export const updateTenantSettingsBulk = async (
  tenantId: string,
  tenantName: string,
  settings: TenantSettingUpdate[],
): Promise<ResponseModel<TenantSettingsDocument>> => {
  try {
    const request: UpdateTenantSettingsRequest = {
      tenantId,
      tenantName,
      settings,
    };

    const response = await api.put<ResponseModel<TenantSettingsDocument>>('/tenantservice/TenantSetting/settings', request);

    if (!response.data.isSuccess) {
      throw new Error(response.data.message || 'Failed to update tenant settings');
    }

    return response.data;
  } catch (error: any) {
    console.error('Error updating tenant settings (bulk):', error);
    throw error;
  }
};

// Helper function to convert settings object to setting updates array
export const buildSettingUpdates = (settings: {
  defatomAgent?: Partial<DefatomAgentSettings>;
  remediation?: RemediationSettings;
  complaince?: ComplianceSettings;
  patchBySeverityConfiguration?: PatchBySeverityConfiguration;
  reports?: {
    ReportListingThresholdInDays?: number;
    ReportExpiryMinutes?: Partial<ReportExpiryMinutes>;
  };
  featureList?: FeatureListEntry[];
}): TenantSettingUpdate[] => {
  const updates: TenantSettingUpdate[] = [];

  // Process defatomAgent settings
  if (settings.defatomAgent) {
    Object.entries(settings.defatomAgent).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        updates.push({
          settingPath: `defatomAgent:${key}`,
          settingValue: JSON.stringify(value),
        });
      }
    });
  }

  // Process remediation settings
  if (settings.remediation) {
    Object.entries(settings.remediation).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        updates.push({
          settingPath: `remediation:${key}`,
          settingValue: JSON.stringify(value),
        });
      }
    });
  }

  // Process compliance settings
  if (settings.complaince) {
    Object.entries(settings.complaince).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        updates.push({
          settingPath: `complaince:${key}`,
          settingValue: JSON.stringify(value),
        });
      }
    });
  }

  // Process patch severity configuration - granular approach
  if (settings.patchBySeverityConfiguration) {
    // Send severityCVEMapping separately
    if (settings.patchBySeverityConfiguration.severityCVEMapping !== undefined) {
      updates.push({
        settingPath: 'patchBySeverityConfiguration:severityCVEMapping',
        settingValue: JSON.stringify(settings.patchBySeverityConfiguration.severityCVEMapping),
      });
    }

    // Send daysRange separately
    if (settings.patchBySeverityConfiguration.daysRange !== undefined) {
      updates.push({
        settingPath: 'patchBySeverityConfiguration:daysRange',
        settingValue: JSON.stringify(settings.patchBySeverityConfiguration.daysRange),
      });
    }
  }

  if (settings.reports?.ReportListingThresholdInDays !== undefined && settings.reports?.ReportListingThresholdInDays !== null) {
    updates.push({
      settingPath: 'Reports:ReportListingThresholdInDays',
      settingValue: JSON.stringify(settings.reports.ReportListingThresholdInDays),
    });
  }

  if (settings.reports?.ReportExpiryMinutes) {
    Object.entries(settings.reports.ReportExpiryMinutes).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        updates.push({
          settingPath: `Reports:ReportExpiryMinutes:${key}`,
          settingValue: JSON.stringify(value),
        });
      }
    });
  }

  // featureList is an array, so it can only be saved whole (TenantAPI replaces it at that path)
  if (settings.featureList) {
    updates.push({
      settingPath: 'featureList',
      settingValue: JSON.stringify(settings.featureList),
    });
  }

  return updates;
};

// Update all settings at once using NEW bulk API
export const updateAllTenantSettings = async (
  tenantId: string,
  tenantName: string,
  settings: {
    defatomAgent?: Partial<DefatomAgentSettings>;
    remediation?: RemediationSettings;
    complaince?: ComplianceSettings;
    patchBySeverityConfiguration?: PatchBySeverityConfiguration;
    reports?: {
      ReportListingThresholdInDays?: number;
      ReportExpiryMinutes?: Partial<ReportExpiryMinutes>;
    };
    featureList?: FeatureListEntry[];
  },
) => {
  try {
    // Build the settings updates array
    const settingUpdates = buildSettingUpdates(settings);

    // Validate that we have settings to update
    if (settingUpdates.length === 0) {
      throw new Error('No settings to update');
    }

    // Call the bulk update API
    const response = await updateTenantSettingsBulk(tenantId, tenantName, settingUpdates);

    return response.isSuccess;
  } catch (error) {
    console.error('Error updating tenant settings:', error);
    throw error;
  }
};

// Legacy function for backward compatibility (kept for old code)
export const updateAllTenantSettingsLegacy = async (
  tenantId: string,
  settings: {
    defatomAgent: Partial<DefatomAgentSettings>;
    remediation: RemediationSettings;
    complaince: ComplianceSettings;
    patchBySeverityConfiguration: PatchBySeverityConfiguration;
  },
) => {
  try {
    const results = await Promise.all([
      updateDefatomAgentSettings(tenantId, settings.defatomAgent),
      updateRemediationSettings(tenantId, settings.remediation),
      updateComplianceSettings(tenantId, settings.complaince),
      updatePatchBySeverityConfiguration(tenantId, settings.patchBySeverityConfiguration),
    ]);

    return results.every((result) => result.isSuccess);
  } catch (error) {
    console.error('Error updating tenant settings (legacy):', error);
    throw error;
  }
};

// GET: Fetch Tenant Data Processor Types
export const getTenantDataTypes = async (): Promise<string[]> => {
  try {
    const response = await api.get<ResponseModel<string[]>>('/tenantservice/TenantDataProcessor/tenantdatatypes');
    if (!response.data.isSuccess) {
      throw new Error(response.data.message || 'Failed to fetch processor types');
    }
    // Defensive: data may be string or array
    if (Array.isArray(response.data.data)) {
      return response.data.data;
    }
    return [];
  } catch (error) {
    console.error('Error fetching processor types:', error);
    throw error;
  }
};

export const getDashboardTenantsForGlobalAdmin = async (): Promise<DashboardTenant[]> => {
  try {
    const response = await api.get<ResponseModel<DashboardTenant[]>>(`${Project.api}tenantservice/tenant/dashboard/tenants`);

    if (!response.data.isSuccess) {
      throw new Error(response.data.message || 'Failed to fetch tenants');
    }

    return Array.isArray(response.data.data) ? response.data.data : [];
  } catch (error) {
    console.error('Error fetching dashboard tenants:', error);
    throw error;
  }
};

export const getCustomerName = async (tenantId: string): Promise<string> => {
  try {
    const response = await api.get<ResponseModel<any>>(`${Project.authApi}authservice/api/auth/${tenantId}/get-customer-name`);

    if (!response.data.isSuccess) {
      throw new Error(response.data.message || 'Failed to fetch customer name');
    }

    const parsed = parseData<any>(response.data);

    // Normalize common shapes to a plain string
    if (!parsed && parsed !== '') return '';
    if (typeof parsed === 'string') return parsed;
    if (typeof parsed === 'object') {
      if ('tenantName' in parsed && typeof parsed.tenantName === 'string') return parsed.tenantName;
      if ('name' in parsed && typeof parsed.name === 'string') return parsed.name;
      // If API returned an array like ["name"]
      if (Array.isArray(parsed) && parsed.length > 0) return String(parsed[0]);
    }

    return '';
  } catch (error) {
    console.error('Error fetching customer name:', error);
    throw error;
  }
};
